import { useEffect, useMemo, useRef, useState } from 'react';
import './EditUserCalenderRights.css';

const DEFAULT_RIGHTS = ['read', 'write', 'delete'];

export default function EditUserCalenderRights({
    calendarid,
    currentUsername,                     // <— neu: aktueller Username (optional)
    onClose,
    onSaved,                             // erhält { calendarId, rightsByUser, myRights }
    availableRights = DEFAULT_RIGHTS,
}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Rechte (User → Booleans)
    const [rows, setRows] = useState([]); // [{ name: username, rights: {read,write,delete} }]

    // Benutzerliste für Autocomplete
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState('');
    const [allUsers, setAllUsers] = useState([]); // [{ username, label }]
    const userMap = useMemo(
        () => new Map(allUsers.map(u => [u.username, u])),
        [allUsers]
    );

    const [newUser, setNewUser] = useState('');
    const inputRef = useRef(null);

    // Hilfsfunktionen
    const ensureRightShape = (obj = {}) => {
        const out = {};
        for (const r of availableRights) out[r] = !!obj[r];
        return out;
    };
    const mappingToRows = (mapping = {}) =>
        Object.entries(mapping)
            .map(([name, rights]) => ({ name, rights: ensureRightShape(rights) }))
            .sort((a, b) => a.name.localeCompare(b.name, 'de'));
    const rowsToMapping = (list) => {
        const m = {};
        for (const row of list) m[row.name] = ensureRightShape(row.rights);
        return m;
    };

    // Rechte laden (für diesen Kalender)
    useEffect(() => {
        let ignore = false;
        const controller = new AbortController();

        async function loadRights() {
            setError('');
            setLoading(true);
            try {
                const res = await fetch('./loadRights.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ calendarId: calendarid }),
                    signal: controller.signal,
                });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const data = await res.json();

                // akzeptiere { rights: { [calendarId]: {...} } } oder { rights: {...} }
                const root = data?.rights || {};
                const forCal = (root && typeof root === 'object' && root[calendarid]) || root;

                if (!ignore) setRows(mappingToRows(forCal));
            } catch (e) {
                if (!ignore) setError('Konnte Rechte nicht laden: ' + (e?.message || e));
            } finally {
                if (!ignore) setLoading(false);
            }
        }

        loadRights();
        return () => {
            ignore = true;
            controller.abort();
        };
    }, [calendarid]);

    // Benutzerliste laden (für Autocomplete)
    useEffect(() => {
        let ignore = false;
        const controller = new AbortController();

        async function loadUsers() {
            setUsersError('');
            setUsersLoading(true);
            try {
                const res = await fetch('./getUsers.php', { signal: controller.signal });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const data = await res.json();

                const raw = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
                const normalized = raw.map(u => {
                    if (typeof u === 'string') return { username: u, label: u };
                    const username = u?.username ?? u?.name ?? '';
                    const label =
                        u?.displayName ??
                        (u?.email ? `${username} (${u.email})` : username);
                    return { username, label };
                }).filter(u => u.username);

                // Duplikate entfernen
                const seen = new Set();
                const uniq = [];
                for (const u of normalized) {
                    if (seen.has(u.username)) continue;
                    seen.add(u.username);
                    uniq.push(u);
                }

                if (!ignore) setAllUsers(uniq);
            } catch (e) {
                if (!ignore) setUsersError('Konnte Benutzer nicht laden: ' + (e?.message || e));
            } finally {
                if (!ignore) setUsersLoading(false);
            }
        }

        loadUsers();
        return () => {
            ignore = true;
            controller.abort();
        };
    }, []);

    // Dirty-State
    const snapshot = useMemo(() => JSON.stringify(rowsToMapping(rows)), [rows]);
    const [initialSnap, setInitialSnap] = useState('');
    useEffect(() => {
        if (!loading) setInitialSnap(snapshot);
    }, [loading, snapshot]);
    const isDirty = snapshot !== initialSnap;

    // Row-Operationen
    const toggleRight = (name, right) => {
        setRows(prev =>
            prev.map(r => r.name === name
                ? { ...r, rights: { ...r.rights, [right]: !r.rights[right] } }
                : r
            )
        );
    };
    const toggleAllForUser = (name) => {
        setRows(prev =>
            prev.map(r => {
                if (r.name !== name) return r;
                const allOn = DEFAULT_RIGHTS.every(k => r.rights[k]);
                const next = {};
                for (const k of DEFAULT_RIGHTS) next[k] = !allOn;
                return { ...r, rights: next };
            })
        );
    };
    const removeUser = (name) => setRows(prev => prev.filter(r => r.name !== name));

    const addUser = () => {
        const username = (newUser || '').trim();
        if (!username) return;

        if (rows.some(r => r.name.toLowerCase() === username.toLowerCase())) {
            setError(`Benutzer "${username}" existiert bereits in der Liste.`);
            return;
        }

        setRows(prev =>
            [...prev, { name: username, rights: ensureRightShape() }]
                .sort((a, b) => a.name.localeCompare(b.name, 'de'))
        );
        setNewUser('');
        inputRef.current?.focus();
    };

    const resetChanges = () => {
        try {
            const parsed = JSON.parse(initialSnap);
            setRows(mappingToRows(parsed));
        } catch { }
    };

    // Speichern → Parent informieren → schließen
    const saveAndClose = async () => {
        try {
            setSaving(true);
            setError('');

            const rightsByUser = rowsToMapping(rows);
            const payload = {
                calendarId: calendarid,
                rights: rightsByUser,
            };

            const res = await fetch('./saveRights.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const data = await res.json();
            if (!data?.ok) throw new Error(data?.error || 'Unbekannter Fehler');

            // Rechte des aktuellen Users (falls übergeben) extrahieren
            const myRights = currentUsername
                ? ensureRightShape(rightsByUser[currentUsername] || {})
                : undefined;

            // Parent informieren – jetzt kann er sofort canDelete neu berechnen
            onSaved?.({
                calendarId: calendarid,
                rightsByUser,
                myRights,
            });

            onClose?.();
        } catch (e) {
            setError('Speichern fehlgeschlagen: ' + (e?.message || e));
        } finally {
            setSaving(false);
        }
    };

    // Label-Helfer
    const labelFor = (username) => userMap.get(username)?.label || username;

    // ESC = speichern + schließen (wie von dir vorgesehen)
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && saveAndClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, calendarid]);

    return (
        <div className="edit-user-calendar-rights-backdrop" role="dialog" aria-modal="true">
            <div className="edit-user-calendar-rights-modal">
                <header className="eucr-header">
                    <h2>Kalenderrechte bearbeiten</h2>
                    {/* <div className="eucr-meta"><strong>Kalender:</strong> {calendarid ?? '—'}</div> */}
                    <div className="eucr-spacer" />
                    <button className="eucr-close" onClick={saveAndClose} disabled={saving}>
                        {saving ? 'Speichere…' : 'Schließen'}
                    </button>
                </header>

                {(error || usersError) && (
                    <div className="eucr-alert">
                        {error && <div>{error}</div>}
                        {usersError && <div>{usersError}</div>}
                    </div>
                )}

                <main className="eucr-main">
                    {loading ? (
                        <p>Lade Rechte…</p>
                    ) : (
                        <>
                            <div className="eucr-toolbar">
                                <div className="eucr-add">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        list="users-list"
                                        value={newUser}
                                        placeholder={usersLoading ? 'Benutzer laden…' : 'Benutzer hinzufügen…'}
                                        onChange={(e) => setNewUser(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && addUser()}
                                        disabled={usersLoading}
                                    />
                                    <datalist id="users-list">
                                        {allUsers.map(u => (
                                            <option key={u.username} value={u.username} label={u.label} />
                                        ))}
                                    </datalist>
                                    <button className="eucr-btn" onClick={addUser} disabled={usersLoading}>
                                        Hinzufügen
                                    </button>
                                    <button className="eucr-btn ghost" onClick={resetChanges} disabled={!isDirty}>
                                        Änderungen verwerfen
                                    </button>
                                </div>
                            </div>

                            <div className="eucr-table-wrapper">
                                <table className="eucr-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '36px' }} title="Alle für diesen Benutzer umschalten">↕</th>
                                            <th>Benutzer</th>
                                            {availableRights.map((r) => (
                                                <th key={r}>{r}</th>
                                            ))}
                                            <th style={{ width: '1%' }}>Aktionen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length === 0 ? (
                                            <tr>
                                                <td colSpan={availableRights.length + 3} className="eucr-empty">Keine Einträge</td>
                                            </tr>
                                        ) : (
                                            rows.map((row) => (
                                                <tr key={row.name}>
                                                    <td>
                                                        <button
                                                            className="eucr-mini"
                                                            title="Alle Rechte für diesen Benutzer umschalten"
                                                            onClick={() => toggleAllForUser(row.name)}
                                                            disabled={row.name == currentUsername}  // Verhindert Veränderung der eigenen Rechte
                                                        >
                                                            ✓
                                                        </button>
                                                    </td>
                                                    <td className="eucr-username" title={row.name}>
                                                        {labelFor(row.name)}
                                                    </td>
                                                    {availableRights.map((right) => (
                                                        <td key={right} className="eucr-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!row.rights[right]}
                                                                onChange={() => toggleRight(row.name, right)}
                                                                disabled={row.name == currentUsername}  // Verhindert Veränderung der eigenen Rechte
                                                            />
                                                        </td>
                                                    ))}
                                                    <td className="eucr-center">
                                                        <button className="eucr-mini danger" onClick={() => removeUser(row.name)}
                                                            disabled={row.name == currentUsername} >
                                                            Entfernen
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}
